import type React from 'react';
import {Alert, Box, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography} from '@mui/material';
import {Close} from '@mui/icons-material';
import type {SupplierRow} from '../../../types/categoryProfileTypes';
import type {PlanProps} from '../strategyProps';
import {TONE_COLORS} from '../strategyProps';
import {MatrixChart} from '../MatrixChart';
import {AddSupplierField} from './AddSupplierField';

const MAX_SUPPLIERS = 15;

/** Where a supplier the team adds starts on the preferencing matrix, drafted like the top five. */
function startPoint(s: SupplierRow) {
    const share = s.spendSharePct ?? 0;
    const poShare = s.spend > 0 ? (100 * s.poSpend) / s.spend : 0;
    return {
        supplierId: s.supplierId, name: s.name, sharePct: share,
        x: Math.min(100, 50 + (poShare >= 80 ? 10 : 0)), y: Math.min(100, Math.round(share * 20) / 10),
    };
}

/**
 * Dutch Windmill: Kraljic for the category and Supplier Preferencing for the chosen suppliers, read together.
 * Kraljic x = supply risk, y = profit impact. Preferencing x = attractiveness of the account, y = its relative value.
 * The draft takes the five largest suppliers; the team adds or removes suppliers (up to 15).
 */
export const WindmillTool: React.FC<PlanProps> = ({plan, actions, databagId, path, money}) => {
    const {content, derived, canEdit} = plan;
    const read = new Map(derived.preferencing.map((p, i) => [i, p]));
    const add = (s: SupplierRow) => void actions.save('preferencing', [...content.preferencing, startPoint(s)],
        `${s.name} added to the windmill.`);
    const remove = (i: number) => void actions.save('preferencing', content.preferencing.filter((_, j) => j !== i),
        `${content.preferencing[i].name} removed from the windmill.`);

    return (
        <Stack gap={2}>
            {!derived.kraljic.externalRiskConnected && (
                <Alert severity="info">
                    No external risk provider is connected, so supply risk is drafted from supplier concentration only.
                    Drag the point if you know better.
                </Alert>
            )}
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2}}>
                <Box>
                    <Typography variant="subtitle2">{`Kraljic: ${derived.kraljic.quadrant}`}</Typography>
                    <MatrixChart
                        ariaLabel="Kraljic matrix" xLabel="Supply risk" yLabel="Profit impact"
                        quadrants={['Leverage', 'Strategic', 'Routine', 'Bottleneck']}
                        points={[{id: 'category', label: content.meta.name, x: content.kraljic.x, y: content.kraljic.y}]}
                        readOnly={!canEdit}
                        onMove={(_, x, y) => void actions.save('kraljic', {x, y}, 'Kraljic position moved.')}
                    />
                </Box>
                <Box>
                    <Typography variant="subtitle2">Supplier preferencing</Typography>
                    <MatrixChart
                        ariaLabel="Supplier preferencing matrix" xLabel="Attractiveness of the account"
                        yLabel="Relative value of the account"
                        quadrants={['Exploit', 'Core', 'Nuisance', 'Develop']}
                        points={content.preferencing.map((p, i) => ({id: String(i), label: p.name, x: p.x, y: p.y, color: '#6B4EFF'}))}
                        readOnly={!canEdit}
                        onMove={(id, x, y) => void actions.save('preferencing',
                            content.preferencing.map((p, i) => (String(i) === id ? {...p, x, y} : p)), 'Supplier moved.')}
                    />
                </Box>
            </Box>
            <Table size="small" aria-label="Windmill reads">
                <TableHead>
                    <TableRow>
                        <TableCell>Supplier</TableCell>
                        <TableCell align="right">Share of category spend</TableCell>
                        <TableCell>Their view of you</TableCell>
                        <TableCell>Windmill read</TableCell>
                        {canEdit && <TableCell padding="checkbox"/>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {content.preferencing.map((p, i) => (
                        <TableRow key={p.supplierId ?? p.name}>
                            <TableCell>{p.name}</TableCell>
                            <TableCell align="right">{p.sharePct === undefined ? '—' : `${p.sharePct.toFixed(1)}%`}</TableCell>
                            <TableCell>{read.get(i)?.quadrant}</TableCell>
                            <TableCell sx={{color: TONE_COLORS[read.get(i)?.tone ?? 'warn']}}>{read.get(i)?.read}</TableCell>
                            {canEdit && (
                                <TableCell padding="checkbox">
                                    <Tooltip title="Remove from the windmill">
                                        <IconButton size="small" aria-label={`Remove ${p.name}`} onClick={() => remove(i)}>
                                            <Close fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                    {content.preferencing.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={canEdit ? 5 : 4}>
                                <Typography variant="body2" color="text.secondary">No suppliers in the windmill.</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            {canEdit && (content.preferencing.length < MAX_SUPPLIERS ? (
                <AddSupplierField databagId={databagId} path={path} money={money} onAdd={add}
                                  excluded={content.preferencing.map(p => p.supplierId)}/>
            ) : (
                <Typography variant="caption" color="text.secondary">{`The windmill holds up to ${MAX_SUPPLIERS} suppliers.`}</Typography>
            ))}
        </Stack>
    );
};
